import React, {FC} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';

interface TableStyleProps {
  headerAlignment?: 'left' | 'right' | 'center';
  bodyAlignment?: 'left' | 'right' | 'center';
}

interface Props extends TableStyleProps {
  headerContent: Array<Array<React.ReactNode>>;
  bodyContent: Array<Array<React.ReactNode>>;
  className?: string;
  evenSpaceInColumns?: boolean;
  dataTestId?: string;
}

export const Table: FC<Props> = ({
  headerContent,
  bodyContent,
  headerAlignment = 'center',
  bodyAlignment = 'center',
  className,
  evenSpaceInColumns = true,
  dataTestId = 'tableComponent',
}) => {
  return (
    <StyledTable data-testid={dataTestId} className={className}>
      <TableHead>
        {headerContent.map((row, rowIndex) => (
          <HeadRow key={`table-row-${rowIndex}`}>
            {row.map((column, colIndex) => (
              <HeadCell
                key={`table-column-${rowIndex}-${colIndex}`}
                columnsNumber={evenSpaceInColumns ? row.length : undefined}
                headerAlignment={headerAlignment}
              >
                {column}
              </HeadCell>
            ))}
          </HeadRow>
        ))}
      </TableHead>
      <tbody>
        {bodyContent.map((row, rowIndex) => (
          <BodyRow key={`table-row-${rowIndex}`}>
            {row.map((column, colIndex) => (
              <BodyCell
                key={`table-column-${rowIndex}-${colIndex}`}
                columnsNumber={evenSpaceInColumns ? row.length : undefined}
                bodyAlignment={bodyAlignment}
              >
                {column}
              </BodyCell>
            ))}
          </BodyRow>
        ))}
      </tbody>
    </StyledTable>
  );
};

const StyledTable = styled.table`
  border: 1px solid ${COLORS.Grey2};
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
  width: 100%;
`;

const TableHead = styled.thead`
  border-bottom: 1px solid ${COLORS.Grey2};
`;

const HeadRow = styled.tr`
  background-color: ${COLORS.Grey3};
`;

const BodyRow = styled.tr`
  &:nth-child(even) {
    background-color: ${COLORS.Grey5};
  }
`;

const BaseCell = styled.td<{columnsNumber?: number}>`
  padding: 10px 5px;
  word-break: break-all;

  ${({columnsNumber}) =>
    columnsNumber &&
    css`
      width: ${100 / columnsNumber}%;
    `};
`;

const HeadCell = styled(BaseCell)<TableStyleProps>`
  text-align: ${({headerAlignment}) => headerAlignment};
`;

const BodyCell = styled(BaseCell)<TableStyleProps>`
  text-align: ${({bodyAlignment}) => bodyAlignment};

  &:not(&:last-of-type) {
    border-right: 1px solid ${COLORS.Grey2};
  }
`;
